import { Component } from "react";
import PageContext, { updatePageContext, getPageContext } from "../../components/Common/PageContext";
import { Grid2 as Grid, Container, Typography } from "@mui/material";
import {
	fetchReceivePurchaseOrderDetails,
	getAsnBySsccAsync,
	postMultipleReceivesAsync,
} from "../../services/apiHelper";
import { LoadingDialog } from "../../components/Loading/LoadingDialog";
import { setStateAsync } from "../../util/stateHelper";
import { purchaseOrderStatus } from "../../util/purchaseOrderStatus";
import { createOrUpdateReceive } from "../../util/receiveUtil";
import { v4 as uuidv4 } from "uuid";
import { receiveStatus } from "../../util/receiveStatus";
import { snackBarAlerts } from "../../util/snackBarAlert";
import SnackbarAlert from "../../components/Snackbar/SnackbarAlert";
import { groupByPriorityStockFlag } from "../../util/priorityStockUtil";
import ArrivalsCheckinBySsccScanDialog from "../../components/Arrivals/ArrivalsCheckinBySsccScanDialog";
import { getTimestampInLocalTime } from "../../util/dateUtil";
import CheckinIcon from "../../icons/Checkin";
import BarcodeIcon from "../../icons/Barcode";
import { withStyles } from "@mui/styles";
import { ModalWithActionButton } from "../../components/Modal/ModalWithActionButton";
import { isFeatureEnabledForLocation } from "../../util/featureUtil";
import { PriorityStockDialog } from "../../components/PriorityStock/PriorityStockDialog";
import { Axios, AxiosResponse } from "axios";

const styles = (theme) => ({
	checkinIcon: {
		fontSize: "7rem",
		lineHeight: "0px",
	},
});

interface CheckinProps {
	classes?: any;
	graphProfile?: any;
}

interface CheckinState {
	isAlertSeverityError: boolean;
	showErrorAlert: boolean;
	isShowSubmittingArrivalLoading: boolean;
	snackBarAlert: any;
	openPriorityStockDialog: boolean;
	purchaseOrderPriorityStock: { poNumber: string | null; priorityStock: any };
	openArrivalsCheckinBySsccScanDialog: boolean;
	currentScannedBarcode: { value: string | null; isSscc: boolean; relatedAsn: any };
	isAsnLookupLoading: boolean;
	asnList: any[];
	isBlocking: boolean;
	error?: string;
	showSsccDialogCloseConfirmation?: boolean;
}

class Checkin extends Component<CheckinProps, CheckinState> {
	setStateAsync: (state: Partial<CheckinState>) => Promise<void>;
	updatePageContext: (context: any, callback?: () => void) => void;
	getPageContext: () => any;

	constructor(props: CheckinProps) {
		super(props);
		this.state = {
			isAlertSeverityError: false,
			showErrorAlert: false,
			isShowSubmittingArrivalLoading: false,
			snackBarAlert: snackBarAlerts.closed,
			openPriorityStockDialog: false,
			purchaseOrderPriorityStock: { poNumber: null, priorityStock: {} },
			openArrivalsCheckinBySsccScanDialog: false,
			currentScannedBarcode: { value: null, isSscc: false, relatedAsn: null },
			isAsnLookupLoading: false,
			asnList: [],
			isBlocking: false,
		};
		this.setStateAsync = setStateAsync.bind(this);
		this.updatePageContext = updatePageContext.bind(this);
		this.getPageContext = getPageContext.bind(this);
	}

	static contextType = PageContext;

	async componentDidMount() {
		document.addEventListener("onScan", this.onScan);
	}
	async componentWillUnmount() {
		document.removeEventListener("onScan", this.onScan);
	}

	onScan = async (e) => {
		if (this.isArrivalsFeatureEnabled()) {
			const barcode = e.data.BarcodeValue;
			const currentScannedBarcode = { value: barcode, isSscc: false, relatedAsn: null };
			if (barcode.startsWith("00") && barcode.length > 17) {
				currentScannedBarcode.isSscc = true;
				await this.setStateAsync({ currentScannedBarcode });
				await this.ssccScan(currentScannedBarcode);
			} else {
				await this.setStateAsync({
					showErrorAlert: true,
					isAlertSeverityError: false,
					error: "Please scan a valid SSCC barcode.",
				});
			}
		}
	};

	// Related to Receive by SSCC scan
	async ssccScan(currentScannedBarcode) {
		const barcode = currentScannedBarcode.value;

		//SSCC Check if the scanned barcode is SSCC. Then use SSCC Scan check-in for arrivals
		if (currentScannedBarcode.isSscc) {
			const { asnList } = this.state;

			//Search APP memory for ASN by SSCC. If found use it. Otherwise call Receiving API Get ASN By SSCC
			let foundAsn = asnList.find((a) => a.ssccList.find((c) => c === barcode));

			// ASN Not found in memory.
			if (!foundAsn) {
				// API Call to Receiving API Get ASN By SSCC. Then cache it in the APP
				foundAsn = await this.getAsnBySsccAsync(barcode);

				if (!foundAsn || !foundAsn?.invoiceNumber || !foundAsn?.purchaseOrderId) {
					// An ASN record with no Invoice Number or Purchase Order technically means this ASN has a problem.
					await this.setStateAsync({
						showErrorAlert: true,
						isAlertSeverityError: false,
						error: `No ASN found for scanned code ${barcode}`,
					});
					return;
				}
			}
			if (!asnList.find((asn) => asn.asnId === foundAsn.asnId)) asnList.push(foundAsn);

			if (!this.canCheckinShipment(foundAsn)) {
				// PO is not Edi or PO Status is not correct status(has to be either released or partReceived)
				await this.setStateAsync({
					showErrorAlert: true,
					isAlertSeverityError: false,
					error: `ASN for SSCC Package ${barcode} has PO ${foundAsn.purchaseOrderId} that cannot be checked in.`,
				});
				return;
			}

			// Analyse ASN Data for details about receive, arrivals and received packages
			const analysedAsn = this.analyseAsnData(foundAsn, barcode);

			if (!(await this.validateAsn(analysedAsn, barcode))) {
				await this.setStateAsync({
					currentScannedBarcode: { value: null, isSscc: false, relatedAsn: null },
					asnList,
				});
				return;
			}

			await this.setStateAsync({
				openArrivalsCheckinBySsccScanDialog: true,
				currentScannedBarcode: { ...currentScannedBarcode, relatedAsn: { ...foundAsn, analysed: analysedAsn } },
				asnList,
			});
		}
	}

	/* Related to Receive by SSCC scan
	returns
	{
		hasReceive: bool,
		receivedInFull: bool,
		currentScannedSscc: {
			package: {sscc: string, timestamp: date, checkinMethod: string},
			previouslyReceived: bool
		},
		previouslyReceivedPackages: [{sscc: string, timestamp: date, checkinMethod: string}],
		previouslyReceivedPackagesTotal: number,
		toBeReceivedPackages: [{sscc: string, timestamp: date, checkinMethod: string}],
		toBeReceivedPackagesTotal: number,
	};
	 */
	analyseAsnData(asn, scannedSsccBarcode) {
		if (asn.receive) {
			const allPackages =
				asn.receive.arrivals.length > 0
					? asn.receive.arrivals.flatMap((a) => a.packages)
					: asn.ssccList.map((sscc) => {
							return {
								sscc,
								timestamp: null,
								checkinMethod: null,
							};
						});
			const previouslyReceivedPackages = allPackages.filter((p) => p.timestamp);
			const toBeReceivedPackages = allPackages.filter(
				// Filtering out packages in previouslyReceivedPackages. It is possible that on some previous arrivals it was not received.
				// But received later on a later arrival.
				(p) => !p.timestamp && previouslyReceivedPackages.findIndex((rp) => rp.sscc === p.sscc) === -1
			);
			// Doing this because of the same reason metioned when filtering for toBeReceivedPackages
			const currentScannedSsccPackage =
				allPackages.find((p) => p.sscc === scannedSsccBarcode && p.timestamp) ??
				allPackages.find((p) => p.sscc === scannedSsccBarcode && !p.timestamp);

			const receivedInFull = previouslyReceivedPackages.length === asn.ssccList.length;

			return {
				hasReceive: true,
				receivedInFull,
				currentScannedSscc: {
					package: currentScannedSsccPackage,
					previouslyReceived: currentScannedSsccPackage?.timestamp !== null,
				},
				previouslyReceivedPackages,
				previouslyReceivedPackagesTotal: previouslyReceivedPackages.length,
				toBeReceivedPackages,
				toBeReceivedPackagesTotal: toBeReceivedPackages.length,
			};
		}
		return {
			hasReceive: false,
			receivedInFull: false,
			currentScannedSscc: {
				package: {
					sscc: asn.ssccList.find((c) => c === scannedSsccBarcode),
					timestamp: null,
					checkinMethod: null,
				},
				previouslyReceived: false,
			},
			previouslyReceivedPackages: [],
			previouslyReceivedPackagesTotal: 0,
			toBeReceivedPackages: asn.ssccList.map((sscc) => {
				return {
					sscc,
					timestamp: null,
					checkinMethod: null,
				};
			}),
			toBeReceivedPackagesTotal: asn.ssccList.length,
		};
	}

	// Related to Receive by SSCC scan
	// Validates ASN (Analysed ASN)
	async validateAsn(analysedAsn, scannedSsccBarcode) {
		if (analysedAsn.hasReceive && (analysedAsn.isInFull || analysedAsn.currentScannedSscc.previouslyReceived)) {
			let message = "";
			// Asn Received in full (Has a receive and all packages registered as arrived)
			if (analysedAsn.receivedInFull)
				message = `ASN for scanned SSCC package ${scannedSsccBarcode} has been checked-in`;
			// Asn is partially received and the scanned SSCC already received
			else if (analysedAsn.currentScannedSscc.previouslyReceived) {
				const packageScanDate = getTimestampInLocalTime(analysedAsn.currentScannedSscc.package.timestamp);
				message = `SSCC package ${scannedSsccBarcode} has been checked-in on ${packageScanDate}`;
			}

			await this.setStateAsync({
				showErrorAlert: true,
				isAlertSeverityError: false,
				error: message,
			});
			return false;
		}
		return true;
	}

	canCheckinShipment(asn) {
		return (
			asn.purchaseOrderIsEdi &&
			(asn.purchaseOrderStatus === purchaseOrderStatus.released ||
				asn.purchaseOrderStatus === purchaseOrderStatus.partReceived)
		);
	}

	// Related to Receive by SSCC scan
	getAsnBySsccAsync = async (barcode) => {
		try {
			this.setStateAsync({ isAsnLookupLoading: true });
			const response: AxiosResponse = await getAsnBySsccAsync(barcode);
			return response.data;
		} catch (error) {
			await this.setStateAsync({
				isAlertSeverityError: true,
				isAsnLookupLoading: false,
				error: "Could not retrieve ASN by SSCC scan. Please try again and raise a support ticket if issue persists.",
				showErrorAlert: true,
			});
			return null;
		} finally {
			this.setStateAsync({ isAsnLookupLoading: false });
		}
	};

	// Related to Receive by SSCC scan
	// Multi Receives/Arrivals Check-in
	// arrivalsProgress:  [{arrival: API POST ready Arrival Data, asn: Similar Asn Search Result But, scannedSsccList: [] string array of barcodes}]
	handleSsccScanCompleteCheckinAsync = async (arrivalsProgress) => {
		try {
			await this.setStateAsync({ isShowSubmittingArrivalLoading: true });
			const graphProfile = this.props.graphProfile;
			const { staffId, displayName } = graphProfile;
			let allLineItems = [];
			let receiveDetails: any = {};

			/* Usage of reduce: Group receives by PO and INV. 
			   Case for when the same PO and INV are in multiple ASNs. We wish to create single receive with multiple arrivals
			*/

			const receivesPromise = arrivalsProgress.reduce(async (reducedPromise, ap) => {
				const poNumber = ap.asn.purchaseOrderId;
				const invNumber = ap.asn.invoiceNumber;
				const groupId = `${poNumber}-${invNumber}`;

				ap.arrival.createdByUserName = displayName;
				ap.arrival.createdByUserId = staffId;
				const reduced = await Promise.resolve(reducedPromise);
				const targetReceive = reduced.find((r) => `${r.purchaseOrderNumber}-${r.invoiceNumber}` === groupId);
				//Found a matching receive. Add the arrival to it.
				if (targetReceive) targetReceive.arrivals.push(ap.arrival);
				else {
					const receiveDetailsResponse: AxiosResponse = await fetchReceivePurchaseOrderDetails(
						poNumber,
						invNumber,
						null
					);
					receiveDetails = {
						Invoice: receiveDetailsResponse.data.invoice,
						PurchaseOrder: receiveDetailsResponse.data.purchaseOrder,
						Products: receiveDetailsResponse.data.products,
						PriorityStock: receiveDetailsResponse.data.priorityStock,
					};

					if (!receiveDetails.Invoice || !receiveDetails.PurchaseOrder) {
						console.warn("Arrivals check-in: Possible invalid invoice or purchase order. A receive will be skipped");
						return null;
					}

					const invoiceLineItems = receiveDetails.Invoice.lineItems.map((li) => {
						const purchaseOrderItem = receiveDetails.PurchaseOrder.lineItems.find((poli) => poli.sku === li.sku);
						return {
							sku: purchaseOrderItem.sku,
							model: purchaseOrderItem.model,
							description: purchaseOrderItem.description,
							barcode: purchaseOrderItem.barcode,
							unitCost: purchaseOrderItem.unitCostInc,
							invoiced: li.quantity,
							received: 0,
							originalReceived: 0,
						};
					});

					allLineItems = allLineItems.concat(invoiceLineItems);

					const receiveDate = ap.arrival.dateArrived;
					const receiveId = ap.asn.receive ? ap.asn.receive.id : uuidv4();
					const receive = createOrUpdateReceive(
						receiveDetails,
						invoiceLineItems,
						graphProfile,
						receiveDate,
						receiveId,
						null
					);
					receive.arrivals = [ap.arrival];
					receive.status = ap.asn.receive ? ap.asn.receive.status : receiveStatus.arrived;
					reduced.push(receive);
				}

				return reduced;
			}, []);

			const receives = (await Promise.resolve(receivesPromise)).filter((r) => r != null);

			if (receives.length === 0) {
				console.warn(
					"Arrivals check-in: No receives to submit. Possible reason all ASNs have invalid PO or Invoice reference."
				);
				await this.handleCheckinDialogCloseAsync(false);
				return;
			}
			const result = await postMultipleReceivesAsync(receives);
			await this.setStateAsync({
				isShowSubmittingArrivalLoading: false,
				snackBarAlert: snackBarAlerts.success.savingArrival,
			});
			await this.handleCheckinDialogCloseAsync(false);

			if (result) {
				const { Products, PriorityStock } = receiveDetails;
				const { productGroups } = this.getPageContext();
				allLineItems.forEach((li) => {
					const product = Products.find((product) => product.id === li.sku);
					const productGroup = productGroups.find((pg) => pg.id === product?.productGroupId);
					li.productGroup = productGroup?.name;
				});

				// Priority Stock notice
				await this.showPriorityStock(PriorityStock, null, allLineItems);
			}
		} catch (e) {
			await this.setStateAsync({ snackBarAlert: snackBarAlerts.error.savingArrival });
			console.log(e); // TODO: Proper error handling log to appInsights
		} finally {
			await this.handleCheckinDialogCloseAsync(false);
			await this.setStateAsync({ isShowSubmittingArrivalLoading: false });
		}
	};

	async showPriorityStock(priorityStock, poNumber, lineItems) {
		if (priorityStock && priorityStock.length > 0) {
			const groupedPriorityStock = groupByPriorityStockFlag(priorityStock, lineItems);
			const poPriorityStock = { priorityStock: groupedPriorityStock, poNumber };
			await this.setStateAsync({ openPriorityStockDialog: true, purchaseOrderPriorityStock: poPriorityStock });
		}
	}

	clearSsccDialogRelatedStateAsync = async () => {
		await this.setStateAsync({
			openArrivalsCheckinBySsccScanDialog: false,
			currentScannedBarcode: { value: null, isSscc: false, relatedAsn: null },
			asnList: [],
			isBlocking: false,
		});
	};
	// Arrivals: This is a common close handler for both dialogs. The Normal check-in dialog and SSCC scan check-in dialog
	handleCheckinDialogCloseAsync = async (showConfirm) => {
		this.setStateAsync({ showSsccDialogCloseConfirmation: showConfirm, isBlocking: showConfirm });

		if (!showConfirm) {
			await this.clearSsccDialogRelatedStateAsync();
		}
	};

	handleCloseAlertAsync = async () => {
		return await this.setStateAsync({ showErrorAlert: false, error: null, isAlertSeverityError: false });
	};

	handleCloseConfirmationAsync = async (confirmed) => {
		this.setStateAsync({ showSsccDialogCloseConfirmation: false, isBlocking: false });
		if (confirmed) {
			await this.clearSsccDialogRelatedStateAsync();
			return true;
		}
		return false;
	};

	handleCloseSnackbarAlertAsync = async () => {
		await this.setStateAsync({ snackBarAlert: snackBarAlerts.closed });
	};
	handlePriorityStockDialogCloseAsync = async () => {
		await this.setStateAsync({ openPriorityStockDialog: false });
	};
	isArrivalsFeatureEnabled = () => {
		const {
			userLocation,
			featureConfiguration: { arrivals },
		} = this.getPageContext();

		return isFeatureEnabledForLocation(userLocation, arrivals);
	};

	render() {
		const { classes } = this.props;
		return (
			<Container disableGutters maxWidth="md">
				<Grid container direction="column" justifyContent="center" alignItems="center" spacing={2}>
					<Grid>
						<div className={classes.checkinIcon}>
							<CheckinIcon width="100px" height="100px" />
						</div>
					</Grid>
					<Grid>
						<Typography variant="body1" component="div">
							<p>
								Scan SSCC barcodes to check in
								<br />
								multiple shipments at once!
							</p>
							<p>
								SSCC barcodes start with (00)
								<br />
								and are up to 20 digits in total
							</p>
						</Typography>
					</Grid>
					<Grid>
						<div className={classes.checkinIcon}>
							<BarcodeIcon />
						</div>
					</Grid>
					<Grid>
						<Typography variant="body1" component="div">
							<p>(00)0123456789012345678</p>
						</Typography>
					</Grid>
				</Grid>
				<SnackbarAlert
					open={this.state.snackBarAlert.isOpen}
					message={this.state.snackBarAlert.message}
					severity={this.state.snackBarAlert.severity}
					onClose={this.handleCloseSnackbarAlertAsync}
				/>

				{this.state.showSsccDialogCloseConfirmation && (
					<ModalWithActionButton
						title="Warning"
						alertType="warning"
						primaryButtonName="Yes"
						secondaryButtonName="No"
						onPrimaryButtonClick={() => this.handleCloseConfirmationAsync(true)}
						onSecondaryButtonClick={() => this.handleCloseConfirmationAsync(false)}
						isOpen={this.state.showSsccDialogCloseConfirmation}
						message="Are you sure you want to exit?"
					/>
				)}

				{this.state.showErrorAlert && (
					<ModalWithActionButton
						title={this.state.isAlertSeverityError ? "Error" : "Warning"}
						alertType={this.state.isAlertSeverityError ? "error" : "warning"}
						primaryButtonName="Ok"
						onPrimaryButtonClick={() => this.handleCloseAlertAsync()}
						isOpen={this.state.showErrorAlert}
						message={this.state.error}
					/>
				)}

				{this.state.isShowSubmittingArrivalLoading && (
					<LoadingDialog message="Submitting arrival..." isOpen={this.state.isShowSubmittingArrivalLoading} />
				)}
				{this.state.isAsnLookupLoading && (
					<LoadingDialog message={"Loading...."} isOpen={this.state.isAsnLookupLoading} />
				)}

				{this.state.openPriorityStockDialog && (
					<PriorityStockDialog
						isOpen={this.state.openPriorityStockDialog}
						onClose={this.handlePriorityStockDialogCloseAsync}
						purchaseOrderPriorityStock={this.state.purchaseOrderPriorityStock}
					/>
				)}

				{this.state.openArrivalsCheckinBySsccScanDialog && (
					<ArrivalsCheckinBySsccScanDialog
						isOpen={this.state.openArrivalsCheckinBySsccScanDialog}
						onClose={this.handleCheckinDialogCloseAsync}
						onCompleteCheckin={this.handleSsccScanCompleteCheckinAsync}
						currentScannedBarcode={this.state.currentScannedBarcode}
						isBlocking={this.state.isBlocking}
					/>
				)}
			</Container>
		);
	}
}
export default withStyles(styles, { withTheme: true })(Checkin);
